/**
 * WebSocket Hook for Real-time Updates
 *
 * Thin subscriber over the app-wide `WebSocketProvider` (see
 * `contexts/WebSocketContext.tsx`), which owns the single `/api/ws`
 * connection, its reconnect/backoff lifecycle, and the shared status signal.
 * This hook just registers/deregisters a subscriber and exposes the shared
 * status.
 */

import { createEffect, on, onCleanup, onMount } from "solid-js";
import type { BoardEvent } from "../api/types";
import {
  useWebSocketContext,
  type WebSocketStatus,
} from "../contexts/WebSocketContext";

export type { WebSocketStatus };

/** Options for useWebSocket hook */
export interface UseWebSocketOptions {
  /** Callback when a board event is received */
  onMessage: (event: BoardEvent) => void;
  /** Callback when an error occurs */
  onError?: (error: Event) => void;
  /** Callback when connection status changes */
  onStatusChange?: (status: WebSocketStatus) => void;
  /**
   * @deprecated Reconnection and backoff are owned by `WebSocketProvider`
   * now; this option has no effect and is kept only for signature
   * compatibility.
   */
  reconnectDelay?: number;
  /**
   * @deprecated Reconnection and backoff are owned by `WebSocketProvider`
   * now; this option has no effect and is kept only for signature
   * compatibility.
   */
  maxReconnectDelay?: number;
  /** Whether to subscribe to the shared connection on mount (default: true) */
  autoConnect?: boolean;
}

/** Return type for useWebSocket hook */
export interface UseWebSocketResult {
  /** Current connection status of the shared connection */
  status: () => WebSocketStatus;
  /** Manually (re-)subscribe this consumer to the shared connection */
  connect: () => void;
  /** Manually unsubscribe this consumer from the shared connection */
  disconnect: () => void;
}

/**
 * Hook for receiving real-time board updates over the shared `/api/ws`
 * connection owned by `WebSocketProvider`.
 *
 * Must be used within a `WebSocketProvider`.
 *
 * @example
 * ```tsx
 * const { status } = useWebSocket({
 *   onMessage: (event) => {
 *     switch (event.type) {
 *       case 'WorkItemCreated':
 *         // Handle new item
 *         break;
 *       // ...
 *     }
 *   }
 * });
 * ```
 */
export function useWebSocket(options: UseWebSocketOptions): UseWebSocketResult {
  const { onMessage, onError, onStatusChange, autoConnect = true } = options;

  const context = useWebSocketContext();

  let unsubscribe: (() => void) | null = null;

  const connect = () => {
    if (unsubscribe) return;
    unsubscribe = context.subscribe(onMessage, onError);
  };

  const disconnect = () => {
    unsubscribe?.();
    unsubscribe = null;
  };

  onMount(() => {
    if (autoConnect) {
      connect();
    }
  });

  onCleanup(() => {
    disconnect();
  });

  if (onStatusChange) {
    // `defer: true` skips the initial run so only actual transitions of the
    // shared status are reported, mirroring the old per-consumer callback's
    // "on transition" behavior.
    createEffect(on(context.status, onStatusChange, { defer: true }));
  }

  return {
    status: context.status,
    connect,
    disconnect,
  };
}
